import { createContext, FC, useContext, useState, ReactNode } from 'react';

type SnackbarSeverity = 'success' | 'info' | 'warning' | 'error';

type SnackbarMessage = string | ReactNode;

interface SnackbarContextType {
  open: boolean;
  toggleSnackbar: (severity?: SnackbarSeverity, message?: SnackbarMessage) => void;
  message: SnackbarMessage;
  severity: SnackbarSeverity;
}

export const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);

  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }

  return context;
};

interface SnackbarProviderProps {
  children: ReactNode;
}

export const SnackbarProvider: FC<SnackbarProviderProps> = ({ children }) => {
  const [message, setMessage] = useState<SnackbarMessage>();
  const [severity, setSeverity] = useState<SnackbarSeverity>('info');
  const [open, setOpen] = useState(false);

  const toggleSnackbar = (severity?: SnackbarSeverity, message?: SnackbarMessage) => {
    if (severity === undefined || message === undefined) {
      setOpen(false);
      return;
    }

    setMessage(message);
    setSeverity(severity);
    setOpen(!open);
  };

  return (
    <SnackbarContext.Provider value={{ message, severity, open, toggleSnackbar }}>{children}</SnackbarContext.Provider>
  );
};
